import { AllowedProvider, allowedProvidersSchema } from '../constants/common';

import { AuthContextProvider, Channel, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { isLocalHost } from '../utils/isLocalHost';
import { connectTiktokAccount } from './enableTiktok';
import { ensureOBSPresence } from '../utils/ensureOBSPresence';
import { AuthServer } from '../services/AuthServer';
import { promisefy } from '../utils/promisefy';
import { enableStreamDestination } from './enableDestination';
import { Analytics } from '../services/AnalyticsService';
import { AbortedError, TransientError } from '../types/errors';
export const connectPlatformSchema = z.object({
  provider: allowedProvidersSchema,
});

export function getConnectPlatformAccountURL({
  serviceEnvironment,
  provider,
  returnUrl: returnUrlParam,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  provider: AllowedProvider;
  returnUrl?: string;
}) {
  const url = new URL(isLocalHost() ? 'http://localhost:3000' : window.location.href);
  url.search = '';
  url.pathname = `/auth/${provider}/success`;

  const returnUrl = returnUrlParam ? encodeURIComponent(returnUrlParam) : encodeURIComponent(url.toString());
  return `${serviceEnvironment.citadelV1ApiBaseUrl}/add/${provider}?return=${returnUrl}`;
}

export function getLoginURL({
  serviceEnvironment,
  provider,
  returnUrl: returnUrlParam,
}: {
  serviceEnvironment: ServiceEnvironment;
  provider: AllowedProvider;
  returnUrl?: string;
}) {
  const url = new URL(isLocalHost() ? 'http://localhost:3000' : window.location.href);
  url.search = '';
  url.pathname = `/auth/${provider}/success`;

  const returnUrl = returnUrlParam ? encodeURIComponent(returnUrlParam) : encodeURIComponent(url.toString());
  return `${serviceEnvironment.citadelV1ApiBaseUrl}/auth/${provider}?return=${returnUrl}`;
}

export function useConnectPlatformAccount({
  authContextProvider,
  serviceEnvironment,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
}) {
  return useMutation({
    mutationFn: async (values: { provider: AllowedProvider; returnUrl?: string }) => {
      const { provider, returnUrl } = values;
      const url = getConnectPlatformAccountURL({ authContextProvider, serviceEnvironment, provider, returnUrl });
      window.location.href = url;
    },
  });
}

/**
 * The way it works with connecting an account for the sake of the designed UX flow is that we have a route
 * that is responsible for opening the regular StreamElements auth flow, but it provides our own callback route with a port number as query parameter.
 * Once the auth flow is complete, the user has a new cookie with the account they logged in with, and now they're redirected to our /callback route.
 * On this callback route, we just fetch the current channel that is logged in, and then POST it to our server that we create in this function.
 * Once we do that, we have all the data that we need to create a new destination and outputs for it.
 * So to illustrate:
 * 1. User clicks on "Connect with ..." button
 * 2. We open the StreamElements auth flow in a new browser window
 * 3. The user logs in and is redirected to our /callback route with the port number as query parameter
 * 4. In the callback route, we fetch the current channel that is logged in
 * 5. We POST it to our server that we create in this function
 * 6. We create a new destination and outputs for it
 * 7. We redirect the user to the success route of our onboarding flow
 */
export async function runAddChannelAuthFlow({
  authContextProvider,
  serviceEnvironment,
  provider,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  provider: AllowedProvider;
}) {
  if (provider === 'tiktok') {
    return connectTiktokAccount({
      authContextProvider,
      serviceEnvironment,
      streamDestination: null,
    });
  }

  await ensureOBSPresence();

  const authServer = new AuthServer({
    provider,
  });

  await authServer.listen();

  window.addEventListener('beforeunload', () => {
    console.info('Disposing auth server');
    authServer.dispose();
  });

  const returnUrl = new URL(isLocalHost() ? 'http://localhost:3000' : window.location.href);
  // const returnUrl = new URL('https://selive.streamelements.com');
  returnUrl.pathname = `/auth/connect/${provider}/callback`;
  returnUrl.search = '';
  returnUrl.searchParams.set('port', authServer.get().portNumber.toString());

  const authUrl = getConnectPlatformAccountURL({
    authContextProvider,
    serviceEnvironment,
    provider,
    returnUrl: returnUrl.toString(),
  });

  if (!authUrl) {
    throw new Error('Failed to get connect platform account URL');
  }

  let removeListener = () => {
    void 0;
  };

  async function dispose() {
    await removeListener();
    await authServer.dispose();
  }

  const authPromise = new Promise<{ channel: Channel }>((resolve, reject) => {
    removeListener = authServer.onConnectCallback(async (result) => {
      if (!result?.channel) {
        return reject(new AbortedError('Output connection aborted'));
      }

      resolve(result);
    });
  });

  await promisefy(window.host.openDefaultBrowser)(authUrl);

  const { channel } = (await authPromise) ?? { channel: null };

  await dispose();

  if (!channel) {
    throw new Error('Failed to connect platform account');
  }

  const { channel: authenticatedChannel } = await authContextProvider.getAuthContext();

  await Analytics.reportMultiStreamingEvent({
    name: 'streaming_account_connected',
    fields: [
      ['user_login_provider', authenticatedChannel.provider],
      ['user_connected_provider', channel.provider],
    ],
  });

  return enableStreamDestination({
    authContextProvider,
    serviceEnvironment,
    source: { id: channel._id },
    streamDestination: null,
  });
}

export async function runLoginFlow({
  serviceEnvironment,
  provider,
}: {
  authContextProvider: AuthContextProvider;
  serviceEnvironment: ServiceEnvironment;
  provider: AllowedProvider;
}) {
  switch (provider) {
    case 'tiktok':
      throw new Error('Tiktok not supported');
    case 'custom':
      throw new Error('Custom not supported');
    default:
      break;
  }

  await ensureOBSPresence();

  const authServer = new AuthServer({
    provider,
  });

  await authServer.listen();

  window.addEventListener('beforeunload', () => {
    console.info('Disposing auth server');
    authServer.dispose();
  });

  // const returnUrl = new URL(isLocalHost() ? 'http://localhost:3000' : window.location.href);
  const returnUrl = new URL('https://selive.streamelements.com');
  returnUrl.pathname = `/auth/connect/${provider}/callback`;
  returnUrl.search = '';
  returnUrl.searchParams.set('port', authServer.get().portNumber.toString());

  const authUrl = getLoginURL({
    serviceEnvironment,
    provider,
    returnUrl: returnUrl.toString(),
  });

  if (!authUrl) {
    throw new Error('Failed to get connect platform account URL');
  }

  let removeListener = () => {
    void 0;
  };

  async function dispose() {
    await removeListener();
    await authServer.dispose();
  }

  const authPromise = new Promise<{ token: string }>((resolve, reject) => {
    setTimeout(
      () => {
        reject(new TransientError('Auth flow timed out'));
      },
      2 * 60 * 1000 // 2 minutes
      // 2000
    );

    removeListener = authServer.onToken(async (result) => {
      if (!result?.token) {
        return reject(new AbortedError('Output connection aborted'));
      }

      resolve(result);
    });
  });

  await promisefy(window.host.openDefaultBrowser)(authUrl);

  await authPromise;

  await dispose();

  return authPromise;
}
